import { useTexture } from '@react-three/drei';
import * as THREE from 'three';
import staticVertex from '../staticVertex.glsl';
import wireframeFragment from './wireframeFragment.glsl';
import textureFragment from './textureFragment.glsl';
import { useRef, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import VideoSphere from './VideoSphere.jsx';
import { INTERIOR_RESOURCES } from '../../assetsDictionary';

export default function SphereEnv( { onIntroAnimationComplete, triggerPlay, hasCameraAccess = true } ) {
    const SPHERE_RADIUS = 500.0;
    const ANIM_DURATION = 7.0;

    const WIRE_IN_START = 0.0;
    const WIRE_IN_END   = 5.0;

    const WIRE_OUT_START = 5.5;
    const WIRE_OUT_END   = 7.0;

    const TEX_START = 1.0;
    const TEX_END = 6.0;

    const VID_START = 2.0;
    const VID_END = ANIM_DURATION;

    const texture = useTexture(INTERIOR_RESOURCES.TexturaInterior.url);
    
    const animationTimeRef = useRef(0);

    const wireframeMaterialRef = useRef();
    const textureMaterialRef = useRef();
    const videoSphereRef = useRef();

    const [showWireframe, setShowWireframe] = useState(true);

    const doneRef = useRef(false);
    
    const ramp = (t, start, end) => {
        if (t <= start) return 0;
        if (t >= end) return 1;
        return (t - start) / (end - start);
    };

    
    useFrame((state, delta) => {

        if (doneRef.current) return;

        animationTimeRef.current = Math.min(animationTimeRef.current + delta, ANIM_DURATION);
        const t = animationTimeRef.current;

        const wireIn  = ramp(t, WIRE_IN_START, WIRE_IN_END);      // 0→1
        const wireOut = 1 - ramp(t, WIRE_OUT_START, WIRE_OUT_END); // 1→0
        const wireAlpha = wireIn * wireOut; // sube y después baja

        const texAlpha = ramp(t, TEX_START, TEX_END);
        const vidAlpha = ramp(t, VID_START, VID_END);

        if (wireframeMaterialRef.current) {
            wireframeMaterialRef.current.uniforms.uAlpha.value = wireAlpha;
        }

        if (textureMaterialRef.current) {
            textureMaterialRef.current.uniforms.uAlpha.value = texAlpha;
        }

        if (videoSphereRef.current?.setAlpha) {
            videoSphereRef.current.setAlpha(vidAlpha);
        }

        if (t >= ANIM_DURATION && !doneRef.current) {
            doneRef.current = true;
            // setShowWireframe(false);
            setTimeout(() => onIntroAnimationComplete?.(), 2000);
        }   

        if (t >= WIRE_OUT_END && showWireframe) {
         setShowWireframe(false);
        }
    }); 

    const lastTriggerRef = useRef(triggerPlay);

    useEffect(() => {
    if (triggerPlay !== lastTriggerRef.current) {
        lastTriggerRef.current = triggerPlay;
        videoSphereRef.current?.play?.();
    }
    }, [triggerPlay]);

    return (
        <>
            {/* Wireframe */}
            {/* TODO: Elimiar esta esfera una vez que haya terminado la animación */}
            {showWireframe && (
                <mesh rotation={[0, THREE.MathUtils.degToRad(70), 0]}>
                    <sphereGeometry args={[SPHERE_RADIUS + 1, 200, 100]} />
                    <shaderMaterial
                        ref={wireframeMaterialRef}
                        vertexShader={staticVertex}
                        fragmentShader={wireframeFragment}
                        uniforms={{
                            uAlpha: { value: 0.0 },
                            uRadius: { value: SPHERE_RADIUS },
                            uColor: { value: hasCameraAccess 
                                ? new THREE.Color(0, 122/255, 229/255)  // azul
                                : new THREE.Color(1, 1, 1)  // blanco
                            }
                        }}
                        side={THREE.BackSide}
                        transparent={true}
                        wireframe={true}
                    />
                </mesh>
            )}

            {/* Fondo */}
            <VideoSphere
                ref={videoSphereRef}
                src={INTERIOR_RESOURCES.VideoDespegue.url}
                loopSrc={INTERIOR_RESOURCES.VideoVuelo.url}
                radius={SPHERE_RADIUS }
                rotation={[0, THREE.MathUtils.degToRad(70), 0]}
            />
            
            {/* Cabina */}
            <mesh rotation={[0, THREE.MathUtils.degToRad(70), 0]}>
                <sphereGeometry args={[SPHERE_RADIUS, 198, 128]} />
                <shaderMaterial
                    ref={textureMaterialRef}
                    vertexShader={staticVertex}
                    fragmentShader={textureFragment}
                    uniforms={{
                        uTexture: { value: texture },
                        uAlpha: { value: 0.0 },
                        uRadius: { value: SPHERE_RADIUS }
                    }}
                    side={THREE.BackSide}
                    transparent={true}
                />
            </mesh>
        </>
    );
}